// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PaperclipIcon, X, FileIcon, Loader2, Download, Eye } from 'lucide-react';
import { uploadAttachment, deleteTransactionAttachment } from '@/utils/fileUpload';
import { downloadAttachment } from '@/utils/fileDownload';
import { Attachment } from '@/types/transaction';
import { toast } from 'react-hot-toast';
// Migrated: Using native Date instead of Firebase Timestamp
// Migrated: Using native Date instead of Firebase Timestamp

interface AttachmentUploadProps {
  userId: string;
  transactionId: string;
  attachments: Attachment[];
  onAttachmentChange: (attachments: Attachment[]) => void;
  disabled?: boolean;
}

export function AttachmentUpload({
  userId,
  transactionId,
  attachments,
  onAttachmentChange,
  disabled = false,
}: AttachmentUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      // Upload the file and get the attachment info
      const newAttachment = await uploadAttachment(userId, transactionId, file);
      if (!newAttachment) {
        throw new Error('Failed to upload attachment');
      }

// Migrated from: // Migrated from:       const now = Timestamp.now().toDate();
// Migrated from:       const now = new Date();
// Migrated from:       const now = Timestamp.now();
      const now = new Date();
      
      // Format attachment to match database schema
      const formattedAttachment: Attachment = {
        id: newAttachment.id,
        name: file.name,
        type: file.type,
        url: newAttachment.fileUrl || '',
        fileName: newAttachment.fileName,
        fileUrl: newAttachment.fileUrl || '',
        fileType: newAttachment.fileType || 'application/octet-stream',
        uploadedAt: now,
        size: newAttachment.size || 0,
        userId: userId,
        createdAt: now,
        updatedAt: now
      };

      console.log('Formatted attachment:', formattedAttachment);

      // Ensure attachments is always an array and contains valid data
      const currentAttachments = Array.isArray(attachments) ? attachments : [];
      const updatedAttachments = [...currentAttachments, formattedAttachment];
      
      // Only update local state
      onAttachmentChange(updatedAttachments);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = async (attachmentToRemove: Attachment) => {
    try {
      console.log('Removing attachment:', attachmentToRemove);

      // Validate attachment data
      if (!attachmentToRemove || (!attachmentToRemove.id && !attachmentToRemove.fileUrl)) {
        console.error('Invalid attachment data:', attachmentToRemove);
        throw new Error('Invalid attachment data');
      }

      // Update local state first for better UX
      const updatedAttachments = attachments.filter(a => {
        if (attachmentToRemove.id && a.id === attachmentToRemove.id) return false;
        if (attachmentToRemove.fileUrl && a.fileUrl === attachmentToRemove.fileUrl) return false;
        return true;
      });
      
      // Only update local state
      onAttachmentChange(updatedAttachments);

      // Delete from storage
      await deleteTransactionAttachment(userId, transactionId, attachmentToRemove);
      toast.success('Attachment removed successfully');
    } catch (error) {
      console.error('Error removing attachment:', error);
      // Revert local state
      onAttachmentChange(attachments);
      toast.error('Failed to remove attachment');
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      await downloadAttachment(
        userId,
        transactionId,
        attachment.id + '.' + attachment.fileName.split('.').pop()!,
        attachment.fileName
      );
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const isPreviewable = (fileName?: string) => {
    if (!fileName) return false;
    return fileName.match(/\.(jpg|jpeg|png|gif|pdf)$/i) !== null;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx"
          disabled={disabled || isUploading}
        />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          className="flex items-center gap-2"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <PaperclipIcon className="h-4 w-4" />
              <span>Attach File</span>
            </>
          )}
        </Button>
      </div>

      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={`${attachment.fileName}-${attachment.fileUrl}`}
              className="flex items-center gap-3 p-2 rounded-lg bg-white dark:bg-[#121826] border border-gray-200 dark:border-gray-700"
            >
              <FileIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {attachment.fileName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(attachment.size)}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {isPreviewable(attachment.fileName) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    onClick={() => window.open(attachment.fileUrl, '_blank')}
                    title="Preview"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  onClick={() => handleDownload(attachment)}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  onClick={() => handleRemoveAttachment(attachment)}
                  disabled={disabled}
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
